import React, { useState, useEffect } from 'react';
import { Crown, Star, TrendingUp, CreditCard, Plane, Building, Headphones, Zap, Lock, Unlock, Gift, Award, ChevronRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import FireworksOverlay from '../components/FireworksOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIPMima = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [vipStatus, setVipStatus] = useState(null);
  const [stakingOptions, setStakingOptions] = useState(null);
  const [userStakes, setUserStakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFireworks, setShowFireworks] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [staking, setStaking] = useState(false);

  // Theme colors
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const textSubtle = isDark ? 'text-white/50' : 'text-gray-500';
  const cardBg = isDark ? 'bg-black/40' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const inputBg = isDark ? 'bg-black/60' : 'bg-gray-50';

  const benefits = [
    { key: 'ultra_card', icon: CreditCard, name: 'Ultra Debit Card', description: 'Premium metal card with 3% cashback on all purchases', color: 'from-yellow-500 to-amber-600' },
    { key: 'lounge_access', icon: Plane, name: 'Airport Lounge Access', description: 'Unlimited access to 1000+ airport lounges worldwide', color: 'from-blue-500 to-indigo-600' },
    { key: 'concierge', icon: Headphones, name: 'Personal Concierge', description: '24/7 dedicated concierge service for travel and lifestyle', color: 'from-purple-500 to-pink-600' },
    { key: 'hotel_rewards', icon: Building, name: 'Hotel Rewards', description: 'Complimentary upgrades and exclusive rates at luxury hotels', color: 'from-teal-500 to-cyan-600' },
    { key: 'mima_staking', icon: TrendingUp, name: 'MIMA Premium Staking', description: 'Access to high-yield staking with up to 25% APY', color: 'from-green-500 to-emerald-600' },
    { key: 'priority_support', icon: Zap, name: 'Priority Support', description: 'Skip the queue with dedicated VIP support line', color: 'from-orange-500 to-red-600' }
  ];

  const stakingTiers = [
    { tier: 'bronze', name: 'Bronze', apy: 8.5, minStake: 1000, lockDays: 30, color: 'from-amber-700 to-amber-900' },
    { tier: 'silver', name: 'Silver', apy: 12.0, minStake: 10000, lockDays: 60, color: 'from-gray-400 to-gray-600' },
    { tier: 'gold', name: 'Gold', apy: 15.5, minStake: 50000, lockDays: 90, color: 'from-yellow-500 to-yellow-700' },
    { tier: 'platinum', name: 'Platinum', apy: 20.0, minStake: 100000, lockDays: 180, color: 'from-purple-400 to-purple-600' },
    { tier: 'diamond', name: 'Diamond', apy: 25.0, minStake: 500000, lockDays: 365, color: 'from-cyan-400 to-blue-600' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const [statusRes, optionsRes, stakesRes] = await Promise.all([
        axios.get(`${API}/vip/status`, { headers: getAuthHeader() }),
        axios.get(`${API}/mima/staking-options`, { headers: getAuthHeader() }),
        axios.get(`${API}/mima/stakes`, { headers: getAuthHeader() })
      ]);

      setVipStatus(statusRes.data);
      setStakingOptions(optionsRes.data);
      setUserStakes(stakesRes.data.stakes || []);
    } catch (error) {
      console.error('Error fetching VIP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStake = async () => {
    if (!selectedTier || !stakeAmount) return;

    setStaking(true);
    try {
      const response = await axios.post(
        `${API}/mima/stake`,
        { amount: parseFloat(stakeAmount), tier: selectedTier },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        setUserStakes([...userStakes, response.data]);
        setSelectedTier(null);
        setStakeAmount('');
        fetchData();
      }
    } catch (error) {
      console.error('Staking error:', error);
    } finally {
      setStaking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className={`text-xl ${textPrimary}`}>Loading VIP status...</div>
      </div>
    );
  }

  const isVIP = vipStatus?.is_vip;
  const isMembershipActive = vipStatus?.membership_active;

  return (
    <div data-testid="vip-page" className="min-h-screen py-6 px-3 md:px-4">
      <FireworksOverlay show={showFireworks} onComplete={() => setShowFireworks(false)} />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-3 ${isDark ? 'bg-gradient-to-r from-yellow-500/20 to-purple-500/20 border-yellow-500/30' : 'bg-gradient-to-r from-yellow-100 to-purple-100 border-yellow-300'} border rounded-full px-6 py-3 mb-4`}>
            <Crown className="w-8 h-8 text-yellow-500" />
            <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-purple-500">
              MIMA VIP Program
            </span>
          </div>
          <p className={`${textMuted} max-w-2xl mx-auto`}>
            Unlock exclusive benefits, premium staking rates, and luxury perks when you reach $500,000 in trading volume.
          </p>
        </div>

        {/* VIP Progress or Status */}
        {!isVIP ? (
          <div className={`mb-8 ${isDark ? 'bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border-purple-500/30' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'} border rounded-2xl p-6`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'} flex items-center justify-center`}>
                  <TrendingUp className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${textPrimary}`}>Your Progress to VIP</h3>
                  <p className={`${textMuted} text-sm`}>Keep trading to unlock exclusive benefits</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold text-purple-500">
                  ${vipStatus?.trade_volume?.toLocaleString() || 0}
                </div>
                <div className={`${textSubtle} text-sm`}>of $500,000</div>
              </div>
            </div>
            <div className={`w-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded-full h-4 overflow-hidden mb-3`}>
              <div
                className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 rounded-full transition-all duration-1000"
                style={{ width: `${vipStatus?.progress_percent || 0}%` }}
              />
            </div>
            <p className={`${textSubtle} text-sm`}>
              Trade <span className="text-purple-500 font-medium">${vipStatus?.remaining?.toLocaleString()}</span> more to become a VIP member
            </p>
          </div>
        ) : (
          <div className={`mb-8 ${isDark ? 'bg-gradient-to-r from-yellow-900/40 to-amber-900/40 border-yellow-500/30' : 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200'} border rounded-2xl p-6`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-yellow-500 to-amber-600 flex items-center justify-center">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-yellow-500">VIP Member</h3>
                  <p className={textMuted}>Member since {new Date(vipStatus.member_since).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="w-5 h-5 text-yellow-500" />
                  <span className="text-2xl font-mono font-bold text-yellow-500">{vipStatus.points || 0}</span>
                </div>
                <div className={`${textSubtle} text-sm`}>Reward Points</div>
              </div>
            </div>
            {!isMembershipActive && (
              <div className={`mt-4 p-4 ${isDark ? 'bg-black/30 border-yellow-500/20' : 'bg-white border-yellow-200'} rounded-xl border`}>
                <p className={`${textMuted} text-sm mb-3`}>
                  Activate your VIP membership for <span className="text-yellow-500 font-bold">$100/month</span> to access all benefits
                </p>
                <button className="bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-semibold px-6 py-2 rounded-lg hover:opacity-90 transition-opacity">
                  Activate Membership
                </button>
              </div>
            )}
          </div>
        )}

        {/* Benefits Grid */}
        <div className="mb-8">
          <h2 className={`text-xl font-bold ${textPrimary} mb-4 flex items-center gap-2`}>
            <Star className="w-5 h-5 text-yellow-500" />
            VIP Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit) => {
              const Icon = benefit.icon;
              const isUnlocked = isVIP && isMembershipActive;
              return (
                <div
                  key={benefit.key}
                  className={`relative ${cardBg} border rounded-xl p-4 transition-all ${
                    isUnlocked 
                      ? `${isDark ? 'border-white/20 hover:border-white/40' : 'border-gray-300 hover:border-gray-400'}` 
                      : `${cardBorder} opacity-60`
                  }`}
                >
                  {!isUnlocked && (
                    <div className="absolute top-3 right-3">
                      <Lock className={`w-4 h-4 ${textSubtle}`} />
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className={`${textPrimary} font-semibold mb-1`}>{benefit.name}</h3>
                  <p className={`${textSubtle} text-sm`}>{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* MIMA Staking Tiers */}
        <div className="mb-8">
          <h2 className={`text-xl font-bold ${textPrimary} mb-4 flex items-center gap-2`}>
            <TrendingUp className="w-5 h-5 text-green-500" />
            MIMA Premium Staking
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {stakingTiers.map((tier) => {
              const isSelected = selectedTier === tier.tier;
              const canStake = isVIP && isMembershipActive;
              return (
                <button
                  key={tier.tier}
                  onClick={() => canStake && setSelectedTier(isSelected ? null : tier.tier)}
                  disabled={!canStake}
                  className={`relative p-4 rounded-xl border transition-all text-left ${
                    isSelected
                      ? 'border-purple-500 bg-purple-500/10'
                      : canStake
                        ? `${cardBorder} ${cardBg} ${isDark ? 'hover:border-white/30' : 'hover:border-gray-400'}`
                        : `${isDark ? 'border-white/5 bg-black/20' : 'border-gray-100 bg-gray-50'} opacity-50 cursor-not-allowed`
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${tier.color} flex items-center justify-center mb-2`}>
                    <Award className="w-4 h-4 text-white" />
                  </div>
                  <div className={`${textPrimary} font-semibold`}>{tier.name}</div>
                  <div className="text-2xl font-mono font-bold text-green-500">{tier.apy}%</div>
                  <div className={`${textSubtle} text-xs`}>APY</div>
                  <div className={`mt-2 pt-2 border-t ${cardBorder} text-xs ${textMuted}`}>
                    <div>Min: ${tier.minStake.toLocaleString()}</div>
                    <div>Lock: {tier.lockDays} days</div>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <Check className="w-5 h-5 text-purple-500" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Stake Input */}
          {selectedTier && (
            <div className={`mt-4 p-4 ${cardBg} border border-purple-500/30 rounded-xl`}>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className={`${textMuted} text-sm mb-1 block`}>Amount to Stake (USD)</label>
                  <input
                    type="number"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    placeholder={`Min $${stakingTiers.find(t => t.tier === selectedTier)?.minStake.toLocaleString()}`}
                    className={`w-full ${inputBg} border ${cardBorder} rounded-lg px-4 py-3 ${textPrimary} font-mono focus:border-purple-500 focus:outline-none`}
                  />
                </div>
                <button
                  onClick={handleStake}
                  disabled={staking || !stakeAmount}
                  className="bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold px-8 py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {staking ? 'Staking...' : 'Stake Now'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Active Stakes */}
        {userStakes.length > 0 && (
          <div>
            <h2 className={`text-xl font-bold ${textPrimary} mb-4 flex items-center gap-2`}>
              <Unlock className="w-5 h-5 text-purple-500" />
              Your Active Stakes
            </h2>
            <div className="space-y-3">
              {userStakes.map((stake, idx) => (
                <div key={idx} className={`${cardBg} border ${cardBorder} rounded-xl p-4 flex items-center justify-between`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className={`${textPrimary} font-semibold capitalize`}>{stake.tier} Tier</div>
                      <div className={`${textSubtle} text-sm`}>
                        Unlocks: {new Date(stake.unlock_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xl font-mono font-bold ${textPrimary}`}>${stake.amount?.toLocaleString()}</div>
                    <div className="text-green-500 text-sm">+{stake.apy}% APY</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VIPMima;
